import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { Reveal } from './Reveal';
type FAQItem = {
  question: string;
  answer: string;
};
const faqs: FAQItem[] = [{
  question: 'How does Step Class Ready™ work?',
  answer: 'You get instant access to all 6 volumes and 5 bonus kits as high-quality PDF files. Every resource is printable and ready to use in your next class. Open the volume you need, choose your class plan or drill sequence, print it, and teach it. That is the entire workflow.'
}, {
  question: 'Does it work if I’m just starting out as a step instructor?',
  answer: 'Yes. Volume 1 was specifically designed as the foundation every new step instructor needs — covering biomechanics, safety protocols, platform setup, and the 45° Protocol from the ground up. The system works for instructors at every experience level.'
}, {
  question: 'Are the resources really printable?',
  answer: 'Yes. Every single page across all 6 volumes and 5 bonuses is professionally formatted for printing. Class plans, sequence diagrams, BPM charts, alignment posters, safety checklists — all print-ready from day one.'
}, {
  question: 'What if I have a student with knee pain or limited mobility?',
  answer: 'Volume 4 — Low-Impact Step Adaptations — covers exactly this. Complete modification protocols for clients with knee issues, limited mobility, seniors, beginners, and post-rehabilitation students. You will never have to improvise a modification again.'
}, {
  question: 'Are all 5 bonuses really included in the $19 price?',
  answer: 'Yes. All 5 bonus kits are included in your single $19 payment. No upsells required. No hidden fees. Everything listed on this page is included the moment you complete your purchase.'
}, {
  question: 'How long do I have access?',
  answer: 'Lifetime access. You pay once and the system is yours forever. Every future update to any volume or bonus is included at no additional cost.'
}, {
  question: 'What makes this different from free resources online?',
  answer: 'Free online resources are generic fitness content not built for step instructors. Every single page of Step Class Ready™ was built exclusively for step platform teaching — the biomechanics, the choreography structure, the music timing, the cue scripts, the business system. There is nothing like this anywhere online.'
}];
function Item({
  item,
  index
}: {
  item: FAQItem;
  index: number;
}) {
  const [open, setOpen] = useState(false);
  return <Reveal delay={index * 0.035} data-id="element-65" data-mp="2ylqmqx8085d2">
      <div className="overflow-hidden rounded-2xl border border-ink/10 bg-surface shadow-premium" data-id="element-66" data-mp="2yp61fyxrncdy">
        <button className="flex w-full items-center gap-4 p-5 text-left sm:p-6" onClick={() => setOpen(!open)} aria-expanded={open} data-id="element-67" data-mp="2uo2rtu6bacl6">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green text-sm font-bold text-white" data-id="element-68" data-mp="1j59ol72birdc">
            ?
          </span>
          <span className="flex-1 font-semibold text-ink" data-id="element-69" data-mp="2pceabqv9fcn">{item.question}</span>
          <ChevronDownIcon className={`h-5 w-5 shrink-0 text-green-dark transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-70" data-mp="7pgtzzjya61t" />
        </button>
        <AnimatePresence initial={false} data-id="element-71" data-mp="ci07yxmjv375">
          {open && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.22
        }} data-id="element-72" data-mp="hdysub8o632t">
              <p className="px-5 pb-6 pl-16 text-sm leading-7 text-slate sm:px-6 sm:pl-[4.5rem]" data-id="element-73" data-mp="qdlrwqdxewsp">{item.answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </Reveal>;
}
export function FAQ() {
  return <section className="w-full bg-sky px-4 py-20 sm:py-24" data-id="element-74" data-mp="2x5mmnxsdzfhe">
      <div className="mx-auto max-w-3xl" data-id="element-75" data-mp="2x88u7xfa1uzm">
        <Reveal className="text-center" data-id="element-76" data-mp="21v6lyq7ycejb">
          <h2 className="font-display text-4xl font-extrabold tracking-tight text-ink sm:text-5xl" data-id="element-77" data-mp="21u4ytndnw1l8">
            ❓ Frequently Asked <span className="text-green-dark" data-id="element-78" data-mp="1wz92wc5b0u2u">Questions</span>
          </h2>
          <p className="mt-5 text-slate" data-id="element-79" data-mp="2btdzez318egk">Everything you need to know before you buy.</p>
        </Reveal>
        <div className="mt-10 space-y-3" data-id="element-80" data-mp="26vu7wxhbq56o">
          {faqs.map((faq, index) => <Item key={faq.question} item={faq} index={index} data-id="element-81" data-mp="1j5zykpw7qyr6" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}